<!-- eslint-disable vue/no-v-html -->
<script lang="ts">
/**
 * A checkbox field with label on the right
 */
import type { SetupContext } from 'vue';
import type { HekateDisplayField } from '@onshape/www-inertia';
import FieldErrorMessage from './FieldErrorMessage.vue';
import CoordinatorProvider from '../coordinator/CoordinatorProvider';

export default defineComponent({
  components: {
    FieldErrorMessage,
  },
  props: {
    field: {
      required: true,
      type: Object as unknown as () => HekateDisplayField,
    },
    modelValue: {
      required: true,
      type: Boolean,
    },
  },
  emits: ['blur', 'focus', 'update:modelValue'],
  setup(props: { modelValue: boolean; field: HekateDisplayField }, context: SetupContext) {
    const { modelValue, field } = toRefs(props);

    return CoordinatorProvider.get().checkboxField({
      field,
      modelValue,
      context,
    });
  },
});
</script>

<template>
  <div class="field-checkbox field-container">
    <div
      :class="{
        'field-content': true,
        'field-content-is-validated': field.isValidated,
        'field-content-is-errored':
          field.errorMessages.length > 0 && field.interactiveState === 'dirty',
      }"
    >
      <div
        class="field-checkbox-content"
        @click="clickToLateBlur"
        @keypress="clickToLateBlur"
      >
        <div
          :class="{
            'field-checkbox-box': true,
            'field-checkbox-box-error': field.errorMessages.length > 0,
          }"
        >
          <input
            :id="field.htmlName"
            v-model="bubbleModel"
            type="checkbox"
            :html-name="field.htmlName"
            :required="field.isRequired"
            :disabled="field.isDisabled"
            @blur="$emit('blur', field)"
            @focus="setInteractiveState"
          />
        </div>

        <label
          :for="field.htmlName"
          class="field-checkbox-label flex"
        >
          <div
            :class="{
              'small-text-label': isSmallText,
            }"
          >
            <span v-html="$t(field.name, field.htmlName, field.label)" />
            <a
              v-if="isPrivacyPolicy"
              href="https://www.ptc.com/en/documents/policies/privacy"
              target="_blank"
              class="privacy-policy-link"
              tabindex="-1"
              title="View the privacy policy."
              @click.stop
              ><small>[&#8599; ]</small></a
            >

            <a
              v-if="isTermsOfService"
              href="/en/legal/"
              target="_blank"
              class="privacy-policy-link"
              tabindex="-1"
              title="View the terms of service."
              @click.stop
              ><small>[&#8599; ]</small></a
            >
            <sup
              v-if="field.isRequired"
              class="required-indicator"
              title="This field is required"
              >*</sup
            >
          </div>
        </label>
      </div>
      <field-error-message :messages="field.errorMessages" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '../../../style/vars.scss' as vars;

.field-checkbox {
  width: 100%;
  display: flex;
  flex-direction: row;
  margin-top: 4px;
  margin-bottom: 8px;
  border: 1px solid #eee;

  .field-checkbox-content {
    display: flex;
    width: 100%;
    flex-direction: row;
    justify-content: flex-start;
    align-items: stretch;

    .field-checkbox-box {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: row;

      border-right: 1px solid #eee;
      margin-right: 4px;
      padding-top: 10px;
      padding-left: 4px;
      padding-right: 4px;

      &.field-checkbox-box-error {
        margin-bottom: 4px;
      }
    }

    label {
      &.field-checkbox-label {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        flex-direction: row;

        width: 100%;
        font-size: 0.9em;
        padding-left: 4px;
        padding-right: 6px;

        &:hover {
          cursor: pointer;
        }

        div {
          &.small-text-label {
            font-size: 0.75em;
            line-height: 0.99;
            margin-top: 4px;

            .required-indicator {
              font-size: 1em;
            }
          }

          .privacy-policy-link {
            display: inline-block;
            text-decoration: none;
            padding: 2px;
            margin: 2px;

            color: black;
            small {
              display: inline-block;
              text-decoration: none;
              color: black;
            }
            &:hover {
              color: blue;
              text-decoration: underline;
            }
          }
        }
      }
    }
  }

  .field-content {
    width: 100%;
    &.field-content-is-errored {
      .field-checkbox-content {
        border-bottom: 1px solid vars.$color-error;
      }
    }
  }

  .field-content-is-validated {
    border-right: 4px solid vars.$color-onshape-green;
  }
}
</style>
